/**
 * Exception-safe replacements for cytoscape's `batch()` and bulk edge adds.
 *
 * Cytoscape's `batch(callback)` has no try/finally: a throw inside the
 * callback skips `endBatch()`, leaves `_private.batchCount` above zero
 * forever, and from then on nothing is forwarded to the renderer — the canvas
 * keeps its last painted frame and stops responding to pan/zoom while the
 * rest of the app works, recoverable only by a full remount. The realistic
 * trigger is `cy.add()` on an edge whose endpoint node is not in the graph,
 * which cytoscape answers with a plain `throw`.
 *
 * `safeBatch` guarantees the batch always ends; `addEdgesSafely` keeps the
 * dangling-edge case from throwing in the first place. Errors are re-thrown —
 * a silently frozen canvas is the one outcome this module exists to prevent.
 */

import type { Core, EdgeDefinition } from 'cytoscape'

export function safeBatch(cy: Core, callback: () => void): void {
  cy.startBatch()
  try {
    callback()
  } finally {
    cy.endBatch()
  }
}

/**
 * Add edge definitions, dropping (and warning about) any whose source or
 * target node is absent from the graph. Object sets can legitimately hold
 * edges whose endpoints were never persisted or have since been pruned, and
 * several callers assemble nodes and edges from separate queries.
 */
export function addEdgesSafely(cy: Core, edges: EdgeDefinition[]): void {
  const addable: EdgeDefinition[] = []
  const dangling: string[] = []
  for (const edge of edges) {
    const { source, target, id } = edge.data
    if (cy.hasElementWithId(source) && cy.hasElementWithId(target)) {
      addable.push(edge)
    } else {
      dangling.push(id ?? `${source}->${target}`)
    }
  }

  if (dangling.length > 0) {
    console.warn(
      `[Cytoscape] Skipped ${dangling.length} edge(s) with missing endpoints:`,
      dangling.slice(0, 10)
    )
  }
  cy.add(addable)
}
